// Compositor: rasteriza la escena a un PNG con canvas 2D (lee el MISMO modelo en
// % que el preview → export determinista). No usa el pipeline de video.
//
// Orden: fondo → capas por z-order. Imágenes con object-fit cover + esquinas
// redondeadas; formas (rect/círculo/línea); texto con word-wrap (measureText),
// centrado vertical, como el preview. Espera document.fonts.ready antes de dibujar.
//
// CORS: las imágenes se cargan con crossOrigin='anonymous' para no "tintar" el
// canvas (si no, toBlob tira SecurityError). El bucket de Supabase debe permitir
// CORS en GET (lo hace por defecto) — verificar con imágenes reales.

import type { ImageLayer, Layer, Scene, ShapeLayer, TextLayer } from '../scene/types';
import { fontStack } from '../lib/fonts';
import { polyPoints } from '../lib/shapes';
import { SUPABASE_URL } from '../../../config';
import { descargarDesdeBlob } from '../../../utils/descargarArchivo';
import { buildDownloadFileName } from '../../../utils/downloadFileName';

const EXPORT_LONG_SIDE = 1440;
const EXPORT_CAP = 2048;

// Los favicons de Google (el logo_url típico de las marcas que la IA analizó por
// su web) NO mandan CORS → no se pueden leer en el canvas (drawImage → toBlob
// tira SecurityError) y se saltearían. Se rutean por la edge function image-proxy,
// que los re-sirve con CORS. Todo lo demás (bucket propio de Atlas, blob:, data:)
// va directo. Si no hay SUPABASE_URL, no se toca (se comporta como antes).
function drawableSrc(src: string): string {
  try {
    const u = new URL(src);
    const host = u.hostname.toLowerCase();
    const isGoogleFavicon =
      (host === 'www.google.com' && u.pathname === '/s2/favicons') ||
      (host.endsWith('.gstatic.com') && u.pathname === '/faviconV2');
    if (isGoogleFavicon && SUPABASE_URL) {
      return `${SUPABASE_URL}/functions/v1/image-proxy?url=${encodeURIComponent(src)}`;
    }
  } catch {
    /* src no es una URL absoluta (blob:/data:) → directo */
  }
  return src;
}

function loadImg(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`No se pudo cargar la imagen`));
    img.src = drawableSrc(src);
  });
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawImageCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
  radiusPct: number,
) {
  ctx.save();
  if (radiusPct > 0) {
    roundRectPath(ctx, x, y, w, h, (radiusPct / 100) * Math.min(w, h));
    ctx.clip();
  }
  const iw = img.naturalWidth || 1;
  const ih = img.naturalHeight || 1;
  const ir = iw / ih;
  const dr = w / h;
  let sx = 0, sy = 0, sw = iw, sh = ih;
  if (ir > dr) {
    sw = ih * dr;
    sx = (iw - sw) / 2;
  } else {
    sh = iw / dr;
    sy = (ih - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  ctx.restore();
}

function drawShape(
  ctx: CanvasRenderingContext2D,
  l: ShapeLayer,
  x: number,
  y: number,
  w: number,
  h: number,
  targetW: number,
) {
  const sw = (l.strokeWidthPct / 100) * targetW;
  const dashed = l.strokeStyle === 'dashed';

  if (l.kind === 'line') {
    const th = Math.max(sw, 1);
    ctx.fillStyle = l.stroke;
    ctx.fillRect(x, y + h / 2 - th / 2, w, th);
    return;
  }

  ctx.save();
  const pts = polyPoints(l.kind);
  ctx.beginPath();
  if (pts) {
    // Polígono (triángulo, pentágono, hexágono, estrella, flecha): puntos 0..1.
    pts.forEach(([px, py], i) => {
      const X = x + px * w;
      const Y = y + py * h;
      if (i === 0) ctx.moveTo(X, Y);
      else ctx.lineTo(X, Y);
    });
    ctx.closePath();
    ctx.lineJoin = 'round';
  } else if (l.kind === 'circle') {
    ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
  } else {
    // rect con redondeo (radiusPct % del lado menor).
    const r = ((l.radiusPct ?? 0) / 100) * (Math.min(w, h) / 2);
    roundRectPath(ctx, x, y, w, h, r);
  }
  if (l.fill && l.fill !== 'transparent') {
    ctx.fillStyle = l.fill;
    ctx.fill();
  }
  if (sw > 0) {
    ctx.lineWidth = sw;
    ctx.strokeStyle = l.stroke;
    if (dashed) ctx.setLineDash([sw * 2.2, sw * 1.6]);
    ctx.stroke();
  }
  ctx.restore();
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
  const out: string[] = [];
  for (const para of text.split('\n')) {
    const words = para.split(' ');
    let line = '';
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxW && line) {
        out.push(line);
        line = word;
      } else {
        line = test;
      }
    }
    out.push(line);
  }
  return out;
}

function drawText(ctx: CanvasRenderingContext2D, l: TextLayer, targetW: number, targetH: number) {
  const fontSizePx = (l.fontSizePct / 100) * targetH;
  const boxW = (l.wPct / 100) * targetW;
  const cx = (l.xPct / 100) * targetW;
  const cy = (l.yPct / 100) * targetH;
  const weight = l.fontWeight === 'bold' ? '700' : '400';
  const style = l.fontStyle === 'italic' ? 'italic ' : '';
  ctx.font = `${style}${weight} ${fontSizePx}px ${fontStack(l.fontFamily)}`;
  ctx.fillStyle = l.color;
  ctx.textBaseline = 'middle';
  ctx.textAlign = l.align;
  const lines = wrapLines(ctx, l.text || '', boxW);
  const lineH = fontSizePx * 1.15;
  const tx = l.align === 'left' ? cx - boxW / 2 : l.align === 'right' ? cx + boxW / 2 : cx;
  let ty = cy - (lines.length * lineH) / 2 + lineH / 2;
  for (const line of lines) {
    ctx.fillText(line, tx, ty);
    ty += lineH;
  }
}

function targetSize(scene: Scene, baseEl: HTMLImageElement | undefined): { W: number; H: number } {
  let W: number, H: number;
  if (baseEl && Math.abs(baseEl.naturalWidth / baseEl.naturalHeight - scene.ratio) < 0.02) {
    W = baseEl.naturalWidth;
    H = baseEl.naturalHeight;
  } else if (scene.ratio >= 1) {
    W = EXPORT_LONG_SIDE;
    H = Math.round(EXPORT_LONG_SIDE / scene.ratio);
  } else {
    H = EXPORT_LONG_SIDE;
    W = Math.round(EXPORT_LONG_SIDE * scene.ratio);
  }
  const s = Math.min(1, EXPORT_CAP / Math.max(W, H));
  return { W: Math.round(W * s), H: Math.round(H * s) };
}

/** Rasteriza la escena completa a un Blob PNG. */
export async function compositeScene(scene: Scene): Promise<Blob> {
  await (document.fonts?.ready ?? Promise.resolve()).catch(() => {});

  // Precargar todas las imágenes (las que fallen se saltean).
  const imgs = new Map<string, HTMLImageElement>();
  await Promise.all(
    scene.layers
      .filter((l): l is ImageLayer => l.type === 'image')
      .map(async (l) => {
        try {
          imgs.set(l.id, await loadImg(l.src));
        } catch (e) {
          console.warn('composite: imagen no cargó, se saltea', e);
        }
      }),
  );

  const { W, H } = targetSize(scene, imgs.get('base') ?? [...imgs.values()][0]);
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No se pudo crear el contexto 2D');

  if (scene.bg && scene.bg !== 'transparent') {
    ctx.fillStyle = scene.bg;
    ctx.fillRect(0, 0, W, H);
  }

  for (const l of scene.layers as Layer[]) {
    const w = (l.wPct / 100) * W;
    const h = (l.hPct / 100) * H;
    const x = (l.xPct / 100) * W - w / 2;
    const y = (l.yPct / 100) * H - h / 2;

    // Rotación alrededor del centro de la capa (mismo pivote que el preview).
    const rot = ((l.rotation ?? 0) * Math.PI) / 180;
    if (rot) {
      const cx = (l.xPct / 100) * W;
      const cy = (l.yPct / 100) * H;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(rot);
      ctx.translate(-cx, -cy);
    }

    if (l.type === 'image') {
      const img = imgs.get(l.id);
      if (img) drawImageCover(ctx, img, x, y, w, h, l.radiusPct);
    } else if (l.type === 'shape') {
      drawShape(ctx, l, x, y, w, h, W);
    } else {
      drawText(ctx, l, W, H);
    }

    if (rot) ctx.restore();
  }

  return await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob devolvió null'))), 'image/png'),
  );
}

/**
 * Descarga la escena como PNG con el MISMO mecanismo que el resto de la app
 * (`utils/descargarArchivo`): en iOS abre la hoja de compartir —Safari ignora el
 * atributo `download` y abría el PNG en otra vista en vez de guardarlo—, y en el resto
 * hace la descarga clásica. El nombre sale de `buildDownloadFileName`, el helper común.
 *
 * Devuelve `false` si el usuario cancela la hoja de compartir: cancelar es cancelar,
 * no es un error que haya que reportarle.
 */
export async function downloadScene(scene: Scene, filename?: string): Promise<boolean> {
  const blob = await compositeScene(scene);
  return descargarDesdeBlob({
    blob,
    fileName: filename ?? buildDownloadFileName({ variantName: 'edicion-grafica', mime: blob.type }),
  });
}
